import RowFilterValueInput from "./RowFilterValueInput";
import {
  getError,
  getRelations,
  getStringifiedRelation,
  needsFirstValue,
  needsSecondValue,
  hasError,
  updateColumnType,
  updateRelation,
} from "./rowFilterItemUtils";
import { ClientRowFilter } from "@avenueops/shared-types";
import { DropdownSelect, TextInput } from "@avenueops/web-components";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ColumnType, Relation } from "@prisma/client";
import * as Popover from "@radix-ui/react-popover";
import _ from "lodash";
import React, { useMemo } from "react";

interface Props {
  onDelete: () => void;
  onChange: (_: Partial<ClientRowFilter>) => void;
  rowFilter: ClientRowFilter;
  index: number;
  columns: string[];
}

/**
 * The component used for displaying an individual row filter in the signal edit form.
 */
export default function RowFilterItem({ rowFilter, onDelete, onChange, index, columns }: Props): JSX.Element {
  // A simple helper function to get the strigified contents of the relation that will be displayed in the tag.
  const getLabel = () => {
    return `${rowFilter.columnName !== undefined && rowFilter.columnName !== null ? rowFilter.columnName : ""} ${getStringifiedRelation(rowFilter.relation)} ${
      needsFirstValue(rowFilter.relation) && rowFilter.value !== undefined && rowFilter.value !== null ? rowFilter.value : ""
    } ${needsSecondValue(rowFilter.relation) ? `and ${rowFilter.secondValue !== undefined ? rowFilter.secondValue : ""}` : ""}`;
  };

  const error = useMemo(() => {
    return hasError(rowFilter);
  }, [rowFilter]);

  return (
    <Popover.Root
      defaultOpen={rowFilter.columnName === ""}
      onOpenChange={(open) => {
        if (!open && error) {
          onDelete();
        }
      }}
    >
      <Popover.Trigger asChild>
        <div className="border rounded-lg border-gray-300 text-gray-600 flex flex-row items-center px-1 min-h-10 justify-between cursor-pointer bg-gray-50 hover:bg-gray-100 data-[state=open]:bg-primary-regular data-[state=open]:text-white">
          <div className="px-3 py-2 pr-6 text-inherit flex flex-row gap-2 items-center justify-center text-center leading-tight text-sm font-medium flex-1 ">
            {getLabel()}
          </div>
          <XMarkIcon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
            className="h-4 w-4 hover:text-red-400 cursor-pointer"
          />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50"
          align="center"
          side="top"
          sideOffset={5}
          collisionPadding={10}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <div className="border shadow-md bg-gray-50 rounded-md p-4 text-gray-600 flex flex-col gap-2">
            <h2 className="text-gray-600 font-medium text-xl">Add Filter</h2>
            <div className="min-w-[150px] bg-gray-100">
              {columns && columns.length > 0 ? (
                <DropdownSelect
                  name="columnNameDropdown"
                  options={columns.map((columnName) => ({ value: columnName, label: columnName }))}
                  selectedValue={rowFilter.columnName}
                  onCreate={(columnName) => ({ label: columnName, value: `{{${columnName}}}` })}
                  onChange={(columnName) => onChange({ columnName })}
                />
              ) : (
                <TextInput
                  placeholder="Column Name"
                  name="columnNameInput"
                  defaultValue={rowFilter.columnName}
                  onChange={(e) => onChange({ columnName: e.target.value })}
                />
              )}
            </div>
            <div className="flex flex-row gap-1 w-full">
              <div className="min-w-[150px] flex-1">
                <DropdownSelect
                  name="columnType"
                  selectedValue={rowFilter.columnType}
                  onChange={(columnType) => {
                    updateColumnType(columnType as ColumnType, rowFilter.relation, onChange, needsFirstValue, needsSecondValue);
                  }}
                  options={Object.keys(ColumnType).map((columnTypeOption) => ({
                    label: `(${_.lowerCase(columnTypeOption)} type)`,
                    value: columnTypeOption,
                  }))}
                />
              </div>
              <div className="min-w-[150px] flex-1">
                <DropdownSelect
                  name="relation"
                  selectedValue={rowFilter.relation}
                  onChange={(relation) => updateRelation(relation as Relation, onChange, needsFirstValue, needsSecondValue)}
                  options={getRelations(rowFilter.columnType).map((relationOption) => ({
                    label: getStringifiedRelation(relationOption),
                    value: relationOption,
                  }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {needsFirstValue(rowFilter.relation) && (
                <div className="min-w-[150px] flex-1">
                  <RowFilterValueInput columnType={rowFilter.columnType} value={rowFilter.value!} onChange={(value: string) => onChange({ value })} />
                </div>
              )}

              {needsSecondValue(rowFilter.relation) && (
                <>
                  <div className="text-sm">and</div>
                  <div className="min-w-[150px] flex-1">
                    <RowFilterValueInput
                      columnType={rowFilter.columnType}
                      value={rowFilter.secondValue!}
                      onChange={(secondValue: string) => onChange({ secondValue })}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <Popover.Arrow className="fill-gray-100 shadow-md" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
